import {observer} from 'mobx-react-lite';
import React from 'react';
import {EAutocompleteOption} from 'webapp-lib/Components';

import {
  CasusAutocomplete,
  ContactAutocomplete,
  UserAutocomplete,
} from '#/Autocomplete';
import {CasusContactAutocomplete} from '#/Autocomplete/CasusContactAutocomplete';
import {ClientAutocomplete} from '#/Autocomplete/ClientAutcomplete';
import {ClientMoneyBankAccountAutocomplete} from '#/Autocomplete/ClientMoneyBankAccountAutocomplete';
import {EFTagsAutocomplete} from '#/Autocomplete/ETagsAutocomplete';
import {OfficeLocationAutocomplete} from '#/Autocomplete/OfficeLocationAutocomplete';
import {SpecialityFieldAutocomplete} from '#/Autocomplete/SpecialityFieldAutocomplete';
import communicationHandledBackend, {
  MinimalContact,
} from '@lexian/api/communication-api';
import {Placeholder} from '_/store/template/template.types';
import {getContactTypeIcon} from '_/utilities/contacts.utils';
import {
  EFAutocomplete,
  notRequiredRule,
  requiredRule,
  UseFormReturnValues,
} from '_/utilities/form';
import {firstOrUndefined} from '_/utilities/listUtility';

type EntityPlaceholderFieldProps = {
  placeholder: Placeholder;
  casusId?: string;
  form: UseFormReturnValues;
  onParentEntityChange: (parentEntityId: string) => void;
};

const getDefaultValue = (
  placeholder: Placeholder
): string | EAutocompleteOption[] | undefined => {
  if (placeholder.multiple && !placeholder.value) {
    if (
      !placeholder.defaultValue ||
      (Array.isArray(placeholder.defaultValue) &&
        placeholder.defaultValue.length === 0)
    ) {
      return [];
    }

    return placeholder.defaultValue.map((it) => ({
      value: it.entityId,
      displayText: it.value,
    })) as EAutocompleteOption[];
  }

  return placeholder.value === undefined
    ? firstOrUndefined(placeholder.defaultValue)?.entityId
    : placeholder.value;
};

const UEntityPlaceholderField = ({
  placeholder,
  casusId,
  form,
  onParentEntityChange,
}: EntityPlaceholderFieldProps): React.ReactElement => {
  const rules = placeholder.optional ? notRequiredRule : requiredRule;
  const defaultValue = getDefaultValue(placeholder);

  const onChange = (itemId: string | undefined) => {
    if (itemId) {
      placeholder.placeholders?.forEach((p) => (p.doNotAutoUpdate = undefined));
    }
    if (itemId !== undefined && !placeholder.multiple) {
      onParentEntityChange(itemId);
    }
  };

  switch (placeholder.type) {
    case 'CONTACT':
    case 'INVOLVED_PARTY':
      return casusId ? (
        <CasusContactAutocomplete
          casusId={casusId}
          control={form.control}
          defaultValue={defaultValue}
          excludeInvolvementTypes={
            placeholder.type === 'CONTACT'
              ? [] // Temp. allow all contacts. Fix in LEXIAN-4598. Initial Exlucion 'INVOLVED_PARTY', 'INVOICE_RECIPIENT'
              : ['CLIENT', 'INVOICE_RECIPIENT']
          }
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          onContactSelection={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      ) : (
        <ContactAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          loadContactCall={communicationHandledBackend.searchContacts}
          mapToOptions={(contact: MinimalContact) => ({
            value: contact.id,
            displayText: contact.displayName,
            Icon: getContactTypeIcon(contact.contactType),
          })}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          onContactSelection={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'CASUS':
      return (
        <CasusAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          searchFiltersPersistenceKey='entityPlaceholderField'
          onCasusChange={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'CLIENT':
      return (
        <ClientAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          onClientsChange={(clients) => {
            if (clients && !Array.isArray(clients)) {
              onChange(clients.id);
            }
          }}
        />
      );
    case 'USER':
      return (
        <UserAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          filterable
          onUserChange={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'OFFICE_LOCATION':
      return (
        <OfficeLocationAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          filterable={true}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          persistenceKey='entityPlaceholderField'
          rules={rules}
          onUserChange={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'SPECIALITY_FIELD':
      return (
        <SpecialityFieldAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          filterable={true}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          persistenceKey='entityPlaceholderField'
          rules={rules}
          onUserChange={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'CLIENT_MONEY_BANK_ACCOUNT':
      return (
        <ClientMoneyBankAccountAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          multiple={placeholder.multiple}
          name={placeholder.key}
          rules={rules}
          onUserChange={(item) =>
            onChange(typeof item === 'string' ? item : item?.id)
          }
        />
      );
    case 'COLLABORATOR':
      return placeholder.multiple ? (
        <EFTagsAutocomplete
          control={form.control}
          defaultValue={defaultValue}
          label={placeholder.label}
          name={placeholder.key}
          options={
            firstOrUndefined(placeholder.defaultValue)?.options?.map(
              (option): EAutocompleteOption => ({
                displayText: option.displayName,
                value: option.entityId,
              })
            ) ?? []
          }
          rules={rules}
        />
      ) : (
        <EFAutocomplete
          control={form.control}
          customOnChange={(event) => onParentEntityChange(event.target.value)}
          defaultValue={defaultValue}
          label={placeholder.label}
          name={placeholder.key}
          options={
            firstOrUndefined(placeholder.defaultValue)?.options?.map(
              (option): EAutocompleteOption => ({
                displayText: option.displayName,
                value: option.entityId,
              })
            ) ?? []
          }
          rules={rules}
        />
      );
    default:
      return <></>;
  }
};
export const EntityPlaceholderField = observer(UEntityPlaceholderField);
